import { useState } from "react";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Archive,
  CheckCircle,
  AlertTriangle,
  Info,
  Shield,
  RotateCcw,
  Database,
} from "lucide-react";
import { ScanAPI, Scan } from "@/api/scans";

interface ArchiveScanProps {
  isOpen: boolean;
  onClose: () => void;
  scan: Scan | null;
  onSuccess?: () => void;
}

export function ArchiveScan({ isOpen, onClose, scan, onSuccess }: ArchiveScanProps) {
  const [isArchiving, setIsArchiving] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleArchive = async () => {
    if (!scan) return;
    
    setIsArchiving(true);
    setError(null);
    
    try {
      const response = await ScanAPI.archive(scan.id);

      if (response.code === '200'){
        setIsArchiving(false);
        setIsComplete(true);
        
        if (onSuccess) {
          onSuccess();
        }
        
        onClose();
          setIsComplete(false);
      } else {
        throw new Error(response.message || 'Failed to archive scan');
      }
    } catch (error) {
      setIsArchiving(false);
      setError(error instanceof Error ? error.message : 'Failed to archive scan. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Scan Archived Successfully!</h3>
          <p className="text-muted-foreground">
            The scan "{scan?.id}" has been archived and moved to the archive section.
          </p>
        </div>
      );
    }

    if (isArchiving) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Archiving Scan...</h3>
          <p className="text-muted-foreground">Moving scan to archive...</p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <AlertTriangle className="w-8 h-8 text-amber-500" />
          </div>
          <div className="flex-1">
            <p className="text-muted-foreground">
              Are you sure you want to archive the scan <strong>{scan?.code}</strong>?
            </p>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Archive className="w-5 h-5 text-orange-600" />
            <span>Archive Scan</span>
          </DialogTitle>
           <DialogDescription>
            This action will move the scan to your archived scans.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
              <div className="mt-4">
          {renderContent()}
        </div>

          {!isArchiving && !isComplete && (
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="rounded-lg"
              >
                Cancel
              </Button>
              <Button
                onClick={handleArchive}
                className="bg-orange-600 hover:bg-orange-700 rounded-lg"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive Scan
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
